<template>
  <div v-if="visible" class="backdrop" :class="{ embedded }" @click.self="embedded ? undefined : close()">
    <div class="modal">
      <div class="hdr">
        <div class="title">Transfer</div>
        <button v-if="!embedded" class="icon-btn tiny" type="button" title="Close" @click="close">x</button>
      </div>

      <div class="body">
        <div class="route">
          <div class="account">
            <span>From</span>
            <strong>{{ sourceLabel }}</strong>
            <small>{{ shortKey(sourceKey) }} · {{ sourceBalance }}</small>
          </div>

          <div class="arrow">-></div>

          <div class="account">
            <span>To</span>
            <strong>{{ destinationLabel }}</strong>
            <small>{{ shortKey(destinationKey) || 'Select destination' }}</small>
          </div>
        </div>

        <label class="field">
          <span>Destination</span>
          <select v-model="destinationMode">
            <option v-if="sourceKind === 'wallet' && user?.public_key" value="main">
              Main wallet · {{ shortKey(user.public_key) }}
            </option>
            <option
              v-for="w in destinationWallets"
              :key="w.public_key"
              :value="w.public_key"
            >
              {{ walletLabel(w) }}
            </option>
            <option value="custom">Custom address</option>
          </select>
        </label>

        <label v-if="destinationMode === 'custom'" class="field">
          <span>Address</span>
          <input v-model.trim="customDestination" placeholder="Solana address" autocomplete="off">
        </label>

        <label class="field">
          <span>Amount</span>
          <input v-model.trim="sol" placeholder="0.01" inputmode="decimal" autocomplete="off">
        </label>

        <div v-if="error || localError" class="error">{{ error || localError }}</div>
        <div v-if="hint" class="hint" :class="hintType">{{ hint }}</div>

        <div class="actions">
          <button
            v-if="sourceKind === 'main'"
            class="primary-btn"
            type="button"
            :disabled="phantomBusy || !canSubmit"
            @click="submitPhantom"
          >
            {{ phantomBusy ? 'Signing...' : 'Send with Phantom' }}
          </button>

          <button
            v-else
            class="primary-btn"
            type="button"
            :disabled="busy || !canSubmit"
            @click="submit"
          >
            {{ busy ? 'Sending...' : 'Send' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import { Connection, PublicKey, SystemProgram, Transaction } from '@solana/web3.js'
import { shortKey, formatLamports } from '@/composables/format'

const props = defineProps({
  visible: { type: Boolean, default: false },
  embedded: { type: Boolean, default: false },
  sourceKind: { type: String, default: 'wallet' },
  source: { type: Object, default: null },
  user: { type: Object, default: null },
  wallets: { type: Array, default: () => [] },
  busy: { type: Boolean, default: false },
  error: { type: String, default: '' },
})

const emit = defineEmits(['submit', 'close'])

const destinationMode = ref('')
const customDestination = ref('')
const sol = ref('')
const localError = ref('')
const hint = ref('')
const hintType = ref('ok')
const phantomBusy = ref(false)
let hintTimer = null

const sourceKey = computed(() => props.sourceKind === 'main'
  ? String(props.user?.public_key || '')
  : String(props.source?.public_key || '')
)
const sourceLabel = computed(() => props.sourceKind === 'main'
  ? 'Main wallet'
  : walletLabel(props.source)
)
const sourceBalance = computed(() => props.sourceKind === 'main'
  ? formatLamports(props.user?.lamports)
  : formatLamports(props.source?.lamports)
)
const destinationWallets = computed(() => {
  const source = sourceKey.value
  return props.wallets.filter(w => w.public_key && w.public_key !== source)
})
const destinationKey = computed(() => {
  if (destinationMode.value === 'main') return String(props.user?.public_key || '')
  if (destinationMode.value === 'custom') return customDestination.value
  return destinationMode.value
})
const destinationWallet = computed(() => props.wallets.find(w => w.public_key === destinationKey.value) || null)
const destinationLabel = computed(() => {
  if (destinationMode.value === 'main') return 'Main wallet'
  if (destinationWallet.value) return walletLabel(destinationWallet.value)
  if (destinationMode.value === 'custom') return 'Custom address'
  return 'Destination'
})
const canSubmit = computed(() => {
  return !!sourceKey.value && !!destinationKey.value && solToLamports(sol.value) > 0
})

function walletLabel(w) {
  const name = String(w?.name || '').trim()
  const pk = String(w?.public_key || '').trim()
  if (!pk) return 'Wallet'
  return name ? `${name} · ${shortKey(pk)}` : `Wallet · ${shortKey(pk)}`
}

function reset() {
  destinationMode.value = props.sourceKind === 'wallet' && props.user?.public_key
    ? 'main'
    : (destinationWallets.value[0]?.public_key || 'custom')
  customDestination.value = ''
  sol.value = ''
  localError.value = ''
  hint.value = ''
  if (hintTimer) clearTimeout(hintTimer)
  hintTimer = null
}

function close() {
  reset()
  emit('close')
}

function setHint(text, type = 'ok') {
  hint.value = text
  hintType.value = type
  if (hintTimer) clearTimeout(hintTimer)
  hintTimer = setTimeout(() => { hint.value = '' }, 1000)
}

function phantomProvider() {
  const anyWindow = window
  if (anyWindow.phantom?.solana?.isPhantom) return anyWindow.phantom.solana
  if (anyWindow.solana?.isPhantom) return anyWindow.solana
  return null
}

function rpcUrls() {
  const configured = String(window.__AIME_SOLANA_RPC__ || '').trim()
  return [
    configured,
    'https://api.mainnet-beta.solana.com',
    'https://solana-rpc.publicnode.com',
    'https://rpc.ankr.com/solana',
    'https://api.devnet.solana.com',
  ].filter(Boolean)
}

async function getFreshBlockhash() {
  let lastError = null
  for (const url of rpcUrls()) {
    try {
      const connection = new Connection(url, 'confirmed')
      const blockhash = await connection.getLatestBlockhash('confirmed')
      return { connection, ...blockhash }
    } catch (e) {
      lastError = e
    }
  }
  throw new Error(lastError?.message || 'Failed to get recent blockhash')
}

async function submitPhantom() {
  localError.value = ''
  if (!canSubmit.value) {
    localError.value = 'Set destination and amount'
    return
  }
  const provider = phantomProvider()
  if (!provider) {
    localError.value = 'Phantom wallet not found'
    return
  }
  phantomBusy.value = true
  try {
    const connected = await provider.connect()
    const from = new PublicKey(connected?.publicKey?.toString?.() || sourceKey.value)
    if (from.toBase58() !== sourceKey.value) {
      throw new Error(`Phantom selected ${shortKey(from.toBase58())}, expected ${shortKey(sourceKey.value)}`)
    }

    const to = new PublicKey(destinationKey.value)
    const lamports = solToLamports(sol.value)
    const tx = new Transaction().add(SystemProgram.transfer({
      fromPubkey: from,
      toPubkey: to,
      lamports,
    }))
    tx.feePayer = from
    const { connection, blockhash, lastValidBlockHeight } = await getFreshBlockhash()
    tx.recentBlockhash = blockhash

    const result = await provider.signAndSendTransaction(tx)
    const signature = String(result?.signature || '')
    if (!signature) throw new Error('Phantom did not return signature')
    connection.confirmTransaction({ signature, blockhash, lastValidBlockHeight }, 'confirmed').catch((e) => {
      console.warn('transfer confirmation check failed:', e)
    })
    setHint(`Sent ${shortKey(signature, 6, 6, 16)}`)
    reset()
    emit('close')
  } catch (e) {
    localError.value = e?.message || 'Phantom transfer failed'
  } finally {
    phantomBusy.value = false
  }
}

function submit() {
  localError.value = ''
  const lamports = solToLamports(sol.value)
  if (!canSubmit.value) {
    localError.value = 'Set destination and amount'
    return
  }
  emit('submit', {
    source_public_key: sourceKey.value,
    destination: destinationKey.value,
    lamports,
  })
}

function solToLamports(value) {
  const raw = String(value || '').trim().replace(',', '.')
  if (!raw) return 0
  if (!/^\d+(\.\d{0,9})?$/.test(raw)) return 0

  const [whole, fraction = ''] = raw.split('.')
  const solPart = Number(whole || 0)
  if (!Number.isSafeInteger(solPart) || solPart < 0) return 0

  const frac = (fraction + '000000000').slice(0, 9)
  const lamports = solPart * 1_000_000_000 + Number(frac || 0)
  if (!Number.isSafeInteger(lamports) || lamports <= 0) return 0
  return lamports
}

watch(
  () => props.visible,
  visible => {
    if (visible) reset()
  }
)
</script>

<style scoped>
.backdrop{
  position:fixed;
  inset:0;
  background:rgba(0,0,0,.62);
  display:flex;
  align-items:center;
  justify-content:center;
  z-index:9999;
}
.backdrop.embedded{position:relative;inset:auto;height:100%;min-height:0;background:transparent;display:block;z-index:auto}
.backdrop.embedded .modal{width:100%;height:100%;border:0;border-radius:0}
.modal{
  width:min(560px, calc(100vw - 32px));
  background:#0b0b0b;
  border:1px solid #222;
  border-radius:10px;
  overflow:hidden;
}
.hdr{
  padding:10px 12px;
  background:#1a1a1a;
  border-bottom:1px solid #222;
  display:flex;
  align-items:center;
  justify-content:space-between;
  gap:8px;
}
.title{
  font-weight:600;
  font-size:13px;
  color:#fff;
}
.body{
  padding:12px;
  display:flex;
  flex-direction:column;
  gap:12px;
}
.route{
  display:grid;
  grid-template-columns:minmax(0, 1fr) auto minmax(0, 1fr);
  gap:10px;
  align-items:center;
}
.account{
  min-width:0;
  border:1px solid #252525;
  border-radius:8px;
  background:#0f0f0f;
  padding:9px 10px;
  display:flex;
  flex-direction:column;
  gap:3px;
}
.account span,
.field span{
  color:#6b7280;
  font-size:11px;
}
.account strong{
  color:#e5e7eb;
  font-size:13px;
  font-weight:600;
  white-space:nowrap;
  overflow:hidden;
  text-overflow:ellipsis;
}
.account small{
  color:#8fa1b5;
  font-size:11px;
  white-space:nowrap;
  overflow:hidden;
  text-overflow:ellipsis;
}
.arrow{
  color:#6b7280;
  font-size:12px;
}
.field{
  display:flex;
  flex-direction:column;
  gap:6px;
}
.field input,
.field select{
  width:100%;
  height:34px;
  border-radius:8px;
  border:1px solid #292929;
  background:#0f0f0f;
  color:#e5e7eb;
  padding:0 10px;
  font-size:13px;
}
.error{
  padding:7px 9px;
  border-radius:7px;
  border:1px solid #4a1f1f;
  background:#2a1111;
  color:#ffb4b4;
  font-size:12px;
}
.hint{
  color:#86efac;
  font-size:12px;
}
.hint.bad{ color:#ffb4b4; }
.actions{
  display:flex;
  justify-content:flex-end;
}
.primary-btn{
  height:32px;
  padding:0 14px;
  border-radius:8px;
  border:1px solid #2563eb;
  background:#2563eb;
  color:#fff;
  font-size:13px;
  cursor:pointer;
}
.primary-btn:disabled{
  opacity:.55;
  cursor:default;
}
.icon-btn{
  background:transparent;
  border:1px solid #333;
  color:#aaa;
  border-radius:6px;
  cursor:pointer;
  padding:0;
}
.icon-btn.tiny{
  height:22px;
  width:22px;
  line-height:20px;
  font-size:11px;
}
.icon-btn:hover{ background:#1a1a1a; }

@media (max-width: 560px) {
  .route{ grid-template-columns:1fr; }
  .arrow{ display:none; }
}
</style>
